import { FC } from 'react';
import useLocalization from 'src/services/localization/useLocalization';
import useToggles from 'src/services/toggles/useToggles';
import getFormattedWeight from 'src/utils/product/getFormattedWeight';
import { getProductTileImage } from 'src/utils/product/getProductTileImage';
import { Product, ProductUnit, Quantity } from '../../../../redux/apiTypes';
import getAmountSuffix from '../../../../utils/product/getAmountSuffix';
import Price from '../../Price/Price';
import FavoriteButton from '../../ProductTile/FavoriteButton/FavoriteButton';
import BigProductCardPurchaseButtons from '../BigProductCardPurchaseButtons/BigProductCardPurchaseButtons';
import { styles } from './BigProductCardHeader.styles';

export interface BigProductCardHeaderProps {
  data: Product;
  amount: number;
  unit: ProductUnit;
  quantity: Quantity;
  bundle: number;
  place: string;
  initialRenderWithAutofocus: boolean;
  isFavorite: boolean;
  itemIsAdded: boolean;
  addButtonTranslationKey?: string;
  onAddToCart(): void;
  onAmountChange(amount: number): void;
  onSetLocalTintingMode(isOn: boolean): void;
  onFavoriteClick(): void;
  subscribedToAvailability: boolean;
  onSubscribeToAvailabilityClick(): void;
  isReplacement: boolean;
  onReplace(): void;
  purchaseText: string;
  purchaseIsAvailable: boolean;
  itemsCanBeAdded: boolean;
}

const BigProductCardHeader: FC<BigProductCardHeaderProps> = props => {
  const {
    data: {
      currency,
      img,
      in_stock,
      price,
      title,
      weight,
      volume,
    },
    amount,
    unit,
    quantity,
    bundle,
    place,
    isFavorite,
    itemIsAdded,
    initialRenderWithAutofocus,
    subscribedToAvailability,
    onAddToCart,
    onAmountChange,
    onSetLocalTintingMode,
    onFavoriteClick,
    onSubscribeToAvailabilityClick,
    isReplacement,
    onReplace,
    purchaseText,
    purchaseIsAvailable,
    itemsCanBeAdded,
  } = props;

  const localize = useLocalization();
  const formattedWeight = getFormattedWeight(unit, weight, volume, localize);

  const { BUNDLES_AVAILABLE } = useToggles();

  return (
    <div
      className='BigProductCardFixedHeader'
      data-marker='Fixed header'
      data-testid='big_product_card_header'
    >
      <img
        alt={title}
        className='BigProductCardFixedHeader__image'
        src={getProductTileImage(img)}
      />
      <div className='BigProductCardFixedHeader__title'>
        {title}
      </div>
      <div className='BigProductCardFixedHeader__weight'>{formattedWeight}</div>
      {
        price && (
          <h6 className='BigProductCardFixedHeader__price'>
            <Price
              price={price}
              currency={currency}
              variant="subhead"
            />
          </h6>
        )
      }
      <div className='BigProductCardFixedHeader__buttonContainer'>
        <div className='BigProductCardFixedHeader__addToCartCardButton'>
          <BigProductCardPurchaseButtons
            isHeaderMode
            amount={amount}
            unit={unit}
            quantity={quantity}
            suffix={getAmountSuffix(unit, bundle, localize, BUNDLES_AVAILABLE)}
            isReplacement={isReplacement}
            itemIsAdded={itemIsAdded}
            initialRenderWithAutofocus={initialRenderWithAutofocus}
            onAddToCart={onAddToCart}
            onChangeAmount={onAmountChange}
            onSetLocalTintingMode={onSetLocalTintingMode}
            place={place}
            onReplace={onReplace}
            purchaseText={purchaseText}
            subscribedToAvailability={subscribedToAvailability}
            onSubscribeToAvailabilityClick={onSubscribeToAvailabilityClick}
            itemIsInStock={in_stock}
            purchaseIsAvailable={purchaseIsAvailable}
            itemsCanBeAdded={itemsCanBeAdded}
          />
        </div>

        <FavoriteButton
          onFavoriteClick={onFavoriteClick}
          isChecked={isFavorite}
          tooltipText={localize('product_tile.favorites.manage_button')}
        />
      </div>

      <style jsx>{styles}</style>
    </div>
  );
};

export default BigProductCardHeader;
